import {
  Autocomplete,
  Checkbox,
  TextField,
  Box,
  Grid,
  Button,
  Card,
  InputLabel,
  Collapse,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useStateSiteData } from "../hooks/useStateSiteData";
import { useFinancialYears } from "../hooks/useFinancialYears";
import { useCompaniesData } from "../hooks/useCompaniesData";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ReportFilter = ({
  open,
  filters,
  onFilterChange,
  onApply,
  onReset,
  showCompaniesFilter = false,
}) => {
  const { states, sites } = useStateSiteData();
  const { companies } = useCompaniesData();
  const financialYearOptions = useFinancialYears();

  // Show all sites initially, filter only when states are selected
  const filteredSites =
    filters.states.length > 0
      ? sites.filter((site) =>
          filters.states.some(
            (selectedState) => selectedState.state === site.state
          )
        )
      : sites;

  const handleFinancialYearChange = (event, newValue) => {
    // Reset date filters when financial year is applied
    if (newValue) {
      onFilterChange("startDateTime", "");
      onFilterChange("endDateTime", "");
    }
    onFilterChange("financialYear", newValue);
  };

  const handleDateChange = (field, value) => {
    // Reset financial year when date filters are applied
    if (value) {
      onFilterChange("financialYear", null);
    }
    onFilterChange(field, value);
  };

  return (
    <Grid item xs={12}>
      <Collapse in={open}>
        <Card
          sx={{ borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
        >
          <Box p={4}>
            {/* First Row - States, Sites, Companies */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Companies Filter - Conditionally Rendered */}
              {showCompaniesFilter && (
                <Grid item xs={12} md={4}>
                  <InputLabel sx={{ mb: 1, fontWeight: 500 }}>
                    Companies
                  </InputLabel>
                  <Autocomplete
                    options={companies}
                    onChange={(event, newValue) =>
                      onFilterChange("companies", newValue)
                    }
                    multiple
                    id="companies-filter"
                    disableCloseOnSelect
                    value={filters.companies || []}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select companies"
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* States Filter */}
              <Grid item xs={12} md={4}>
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>States</InputLabel>
                <Autocomplete
                  options={states}
                  onChange={(event, newValue) => {
                    onFilterChange("states", newValue);
                    if (newValue.length > 0) {
                      const newSelectedSites = filters.sites.filter((site) =>
                        newValue.some(
                          (selectedState) => selectedState.state === site.state
                        )
                      );
                      onFilterChange("sites", newSelectedSites);
                    }
                  }}
                  multiple
                  id="states-filter"
                  disableCloseOnSelect
                  value={filters.states}
                  getOptionLabel={(option) => option.state}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.state}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select states"
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  )}
                />
              </Grid>

              {/* Sites Filter */}
              <Grid item xs={12} md={4}>
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>Sites</InputLabel>
                <Autocomplete
                  options={filteredSites}
                  onChange={(event, newValue) =>
                    onFilterChange("sites", newValue)
                  }
                  multiple
                  id="sites-filter"
                  disableCloseOnSelect
                  value={filters.sites}
                  getOptionLabel={(option) => option.manualStoreCode}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.manualStoreCode}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder={
                        filters.states.length > 0
                          ? "Select sites (filtered by selected states)"
                          : "Select sites (all states)"
                      }
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Second Row - Financial Year, Start Date, End Date */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Financial Year Filter */}
              <Grid item xs={12} md={4}>
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>
                  Financial Year
                </InputLabel>
                <Autocomplete
                  options={financialYearOptions}
                  onChange={handleFinancialYearChange}
                  value={filters.financialYear}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select financial year"
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  )}
                />
              </Grid>

              {/* Start Date & Time Filter */}
              <Grid item xs={12} md={4}>
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>
                  Start Date & Time
                </InputLabel>
                <TextField
                  variant="outlined"
                  name="startDateTime"
                  type="datetime-local"
                  value={filters.startDateTime}
                  onChange={(e) =>
                    handleDateChange("startDateTime", e.target.value)
                  }
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={!!filters.financialYear}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>

              {/* End Date & Time Filter */}
              <Grid item xs={12} md={4}>
                <InputLabel sx={{ mb: 1, fontWeight: 500 }}>
                  End Date & Time
                </InputLabel>
                <TextField
                  variant="outlined"
                  name="endDateTime"
                  type="datetime-local"
                  value={filters.endDateTime}
                  onChange={(e) =>
                    handleDateChange("endDateTime", e.target.value)
                  }
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={!!filters.financialYear}
                  sx={{ borderRadius: 1 }}
                />
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={onReset}
                  sx={{ borderRadius: 1.5, px: 3, py: 0.8 }}
                >
                  Reset Filters
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onApply}
                  endIcon={<ArrowForwardIcon fontSize="small" />}
                  sx={{ borderRadius: 1.5, px: 3, py: 0.8 }}
                >
                  Apply Filters
                </Button>
              </Box>
            </Grid>
          </Box>
        </Card>
      </Collapse>
    </Grid>
  );
};

export default ReportFilter;
